import { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";

type Props = {
  taskId?: string;
  commentId?: string;
  /** T49: upload scoped to a project (no task). */
  projectId?: string;
  onUploaded?: (a: { id: string; filename: string }) => void;
};

// Map server error codes to user-facing copy.
function humanizeUploadError(code: string): string {
  switch (code) {
    case "mime_not_allowed":
      return "this file type isn't allowed.";
    case "size_invalid":
      return "the file is too large (25 MB max).";
    case "forbidden_or_not_found":
      return "you don't have access to attach files to this task.";
    case "unauthorized":
      return "you need to sign in again.";
    default:
      return code;
  }
}

export function AttachmentUpload({
  taskId,
  commentId,
  projectId,
  onUploaded,
}: Props) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);
    setBusy(true);
    try {
      // commentId path isn't wired through the public API surface yet —
      // taskId / projectId are the supported parents. If a future commit
      // adds api.uploadAttachmentForComment, branch here.
      const parent = taskId ? { taskId } : projectId ? { projectId } : null;
      if (!parent) {
        throw new Error("missing_parent");
      }
      const result = await api.uploadAttachment(parent, file);
      if (inputRef.current) inputRef.current.value = "";
      onUploaded?.({ id: result.id, filename: file.name });
      if (taskId)
        qc.invalidateQueries({
          queryKey: queryKeys.attachmentsForTask(taskId),
        });
      if (projectId)
        qc.invalidateQueries({
          queryKey: queryKeys.projectFiles(projectId),
        });
      if (commentId)
        qc.invalidateQueries({
          queryKey: queryKeys.attachmentsForComment(commentId),
        });
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.code !== "unknown" ? e.code : (e.message ?? "unknown"));
      } else {
        setError(e instanceof Error ? e.message : "unknown");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
        disabled={busy}
      />
      {busy && <span className="t-sm t-muted"> Uploading…</span>}
      {error && (
        <p role="alert" style={{ color: "var(--color-danger)" }}>
          Upload failed: {humanizeUploadError(error)}
        </p>
      )}
    </div>
  );
}
